import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, H1, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import ItemCardEstimate from '@/components/GridItemCard/Card/components/ItemCardEstimate/ItemCardEstimate';
import { ItemCardPriceRow } from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';
import { ImageRow } from '@/components/ItemCard/ImageRow';
import SaveSearchContainer from '@/components/SaveSearch/SaveSearchContainer';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useItemOrPriceResultLink } from '@/hooks/useItemOrPriceResultLink';
import { useAppSelector } from '@/redux/hooks';
import { getItemSummarySellerId, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    getHasSimilarItemsGroupsFetchCompleted,
    getSimilarItemsGroupsTotalRecords,
} from '@/redux/modules/similarItemsGroups';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getBroadTitleSearchParams } from '@/utils/urls';

import { SimilarPageCardGrid } from './components/SimilarPageCardGrid/SimilarPageCardGrid';
import { SimilarPageHeaderMeta } from './SimilarPageHeaderMeta';

const ITEM_IMAGE_SIZE_MOBILE = 96;
const ITEM_IMAGE_SIZE_DESKTOP = 128;
const ITEM_IMAGE_QUALITY = 80;

const SimilarPage = () => {
    const formatMessage = useAppFormatMessage();
    const { itemId: itemIdParam } = useParams<{ itemId: string }>();
    const itemId = Number(itemIdParam);

    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const anchorItemUrl = useItemOrPriceResultLink(itemId, true);
    const totalRecords = useAppSelector((state) => getSimilarItemsGroupsTotalRecords(state, itemId));
    const hasSimilarFetchCompleted = useAppSelector((state) => getHasSimilarItemsGroupsFetchCompleted(state, itemId));

    const saveSearchParams = useMemo(() => getBroadTitleSearchParams(title, sellerId), [title, sellerId]);

    const isMobileLayout = useIsMobileLayout();
    const itemImageDimension = isMobileLayout ? ITEM_IMAGE_SIZE_MOBILE : ITEM_IMAGE_SIZE_DESKTOP;

    return (
        <>
            <SimilarPageHeaderMeta itemId={itemId} />
            <div className="m-auto max-w-[1644px] px-md py-md xsMax:px-sm xsMax:py-sm">
                <div className="mb-sm">
                    <H1>
                        {hasSimilarFetchCompleted
                            ? formatMessage(['similarPage', 'totalSimilarItems'], {
                                  values: { totalRecords },
                              })
                            : formatMessage(['similarPage', 'loadingHeading'])}
                    </H1>
                </div>

                <div className="mb-md flex items-start gap-sm smMin:mb-xl smMin:gap-md">
                    <div
                        className={cn(
                            'relative h-[96px] w-[96px] max-w-[96px] min-w-0 shrink-0 overflow-hidden rounded-md border border-border-tertiary smMin:h-[128px] smMin:w-[128px] smMin:max-w-[128px]',
                            "[&_[data-testid='itemCardImage']]:w-full [&_[data-testid='itemCardImage']]:!min-w-0"
                        )}
                    >
                        <ImageRow
                            applyDimensionToUrl
                            className="m-0 block h-full w-full max-w-full min-w-0"
                            dimension={itemImageDimension}
                            imageQuality={ITEM_IMAGE_QUALITY}
                            itemId={itemId}
                            lazyLoadImage={false}
                        />
                    </div>
                    <div className="flex min-w-0 flex-col">
                        <SubtleMonochromeLink
                            aria-label={title}
                            className="mb-2xs line-clamp-2 text-link-monochrome smMin:mb-xs"
                            href={anchorItemUrl}
                            size="md"
                        >
                            {title}
                        </SubtleMonochromeLink>
                        <ItemCardEstimate
                            className="mb-0"
                            itemId={itemId}
                        />
                        <ItemCardPriceRow itemId={itemId} />
                    </div>
                </div>

                {totalRecords > 0 ? (
                    <SimilarPageCardGrid itemId={itemId} />
                ) : !hasSimilarFetchCompleted ? (
                    <SimilarPageCardGrid itemId={itemId} />
                ) : (
                    <div className="flex flex-col items-center gap-xs bg-background-subdued pt-lg pb-lg text-center smMax:px-sm smMin:gap-xs smMin:pb-xl">
                        <H2>{formatMessage(['similarPage', 'noSimilarItems'])}</H2>
                        <BodyPrimary>{formatMessage(['similarPage', 'noSimilarItemsBody'])}</BodyPrimary>
                        <SaveSearchContainer
                            allSearchParams={saveSearchParams}
                            className="mt-sm mobile:w-full"
                            saveSearchParams={saveSearchParams}
                            small
                            tooltipPlacement="bottom"
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default SimilarPage;
